import { ReactNode } from "react";
import { Mui } from "@leap4/frontend";
import { numericFontFamily } from "../../../theme/colors";

const { Box, CircularProgress, Skeleton, Typography, Tooltip } = Mui;

interface IMetricCardProps {
    label: string;
    value: string;
    sub?: string;
    /** Series colour, so the card reads against the chart legend. */
    dotColor?: string;
    badge?: ReactNode;
    help?: string;
    loading?: boolean;
    refreshing?: boolean;
    /** Event cards drive the chart: hover focuses (and previews a hidden series), click toggles it. */
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: () => void;
}

const cardSx = { bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: "12px", p: "16px 18px", minWidth: 0 };

/** The DSP MetricCard; labels use text.secondary instead of text.disabled to stay above 4.5:1. */
export const MetricCard = ({ label, value, sub, dotColor, badge, help, loading, refreshing, onMouseEnter, onMouseLeave, onClick }: IMetricCardProps) => {
    if (loading) {
        return (
            <Box sx={cardSx} aria-busy="true">
                <Skeleton width="50%" height={16} />
                <Skeleton width="70%" height={32} sx={{ mt: 0.5 }} />
                <Skeleton width="40%" height={14} sx={{ mt: 0.5 }} />
            </Box>
        );
    }

    const labelNode = (
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
            {dotColor && <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: dotColor, flexShrink: 0 }} />}
            {label}
        </Typography>
    );

    return (
        <Box sx={{ ...cardSx, cursor: onClick ? "pointer" : undefined }} aria-busy={refreshing || undefined} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick}>
            {help ? (
                <Tooltip title={help} describeChild>
                    <Box tabIndex={0} sx={{ width: "fit-content", borderRadius: 1, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" } }}>
                        {labelNode}
                    </Box>
                </Tooltip>
            ) : (
                labelNode
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                <Typography
                    sx={{
                        fontFamily: numericFontFamily,
                        fontVariantNumeric: "tabular-nums",
                        fontSize: "1.5rem",
                        fontWeight: 600,
                        letterSpacing: "-0.03em",
                        lineHeight: 1,
                        color: "text.primary",
                        opacity: refreshing ? 0.5 : 1,
                        transition: "opacity 0.15s",
                    }}
                >
                    {value}
                </Typography>
                {refreshing && <CircularProgress size={14} thickness={5} aria-label="Updating" />}
                {badge}
            </Box>
            {sub && (
                <Typography sx={{ fontFamily: numericFontFamily, fontSize: "0.6875rem", color: "text.secondary", mt: 0.75 }}>
                    {sub}
                </Typography>
            )}
        </Box>
    );
};
